'use client';

import type { ComponentProps, ReactNode } from 'react';
import { FaTimes } from 'react-icons/fa';

import { ButtonV2 } from '../../button';
import type { ModalProps } from '../components';
import { ModalBase, ModalFoot } from '../components';
import { TextModalBody, TextModalHead } from './components';
import styles from './text-modal.component.module.scss';

// memo:
// モーダルは実装がバラツキやすいので、型を厳しめにしています
// マーケに重点が置かれているような、デザインシステム外のモーダルを作る必要がある場合は、ModalBase をベースに別途モーダルコンポーネントを作成してください

type Props = {
  /**
   * aria-label と タイトル を指定します。 aria-label は `ReactModal.Props.contentLabel` により上書きできます。
   */
  title: string;
  /**
   * TextModalHead のレイアウトを指定します
   */
  titleLayout?: ComponentProps<typeof TextModalHead>['layout'];
  /**
   * タイトルをJSXで描画したいときに使用します。renderTitle が指定されている場合、title は無視されます (aria-label としての title は有効なままです)
   */
  renderTitle?: (() => ReactNode) | ReactNode;
  /**
   * モーダルのボディコンテンツです
   */
  body: (() => ReactNode) | ReactNode;
  /**
   * TextModalBody のレイアウトを指定します
   */
  bodyLayout?: ComponentProps<typeof TextModalBody>['layout'];
  /**
   * モーダルのフッターコンテンツです
   */
  foot?: (() => ReactNode) | ReactNode;
  /**
   * ModalFoot のレイアウトを指定します
   */
  footLayout?: ComponentProps<typeof ModalFoot>['layout'];
  /**
   * モーダルの閉じるボタンを自動フォーカスするかどうかを指定します
   */
  autoFocusCloseButton?: boolean;
  isOpen?: boolean; // 既存の実装を踏襲
} & Omit<ModalProps, 'children' | 'isOpen'>;

export const TextModal = ({
  title,
  titleLayout,
  renderTitle,
  body,
  bodyLayout,
  className,
  foot,
  footLayout,
  autoFocusCloseButton = true,
  isOpen = true,
  ...rest
}: Props) => {
  return (
    <ModalBase
      className={[className, styles.textModal].filter(Boolean).join(' ')}
      contentLabel={title}
      isOpen={isOpen}
      {...rest}
    >
      <ButtonV2
        isIcon
        aria-label="閉じる"
        autoFocus={autoFocusCloseButton}
        className={styles.button}
        icon={FaTimes}
        size="m"
        subVariant="negative"
        variant="tertiary"
        onClick={rest.onRequestClose}
      />
      <TextModalHead layout={titleLayout}>
        {renderTitle
          ? typeof renderTitle === 'function'
            ? renderTitle()
            : renderTitle
          : title}
      </TextModalHead>
      <TextModalBody layout={bodyLayout}>
        {typeof body === 'function' ? body() : body}
      </TextModalBody>
      {foot && (
        <ModalFoot layout={footLayout}>
          {typeof foot === 'function' ? foot() : foot}
        </ModalFoot>
      )}
    </ModalBase>
  );
};
